import type { ReactNode } from "react";

// ─── Raw API response — field names match the backend exactly ──────────────────
export interface FlightRiskApiResponse {
  timeStamp: string | null;
  recapOfPlanOfActionPoaDetailsAndNextStepsIfApplicable: string | null;
  last30DaysReturnOnAdSpendRoas: string | null;
  levelOfUrgencyForThisFlightRisk: string | null;
  kpiThatBestRepresentsTheMainIssue: string[] | null;
  flightRiskStatus: string | null;
  doesTheAccountHaveAWalmartAmOrConnectPartner: string | null;
  mainIssueReasonForFlightRiskFlag: string | null;
  onAScaleOf1To5HowConfidentAreYouInRetainingThisClientWithTheCurrentPlanOfActionPoa: string | null;
  last30DaysOfSales: string | null;
  whichOfTheFollowingFactorsPrimarilyContributedToTheFlightRisk: string | null;
  isAPlanOfActionPoaInPlace: string | null;
  accountManagerEmail?: string | null;
  accountManagerName?: string | null;
  hubLeaderEmail?: string | null;
  hubLeaderName?: string | null;
  emailAddress?: string | null;
  ppcManagerEmail?: string | null;
  ppcManagerName?: string | null;
  accountConfigKpiBestRepresentsTheMainIssue?: string[];
  strategistName?: string | null;
  strategistEmail?: string | null;
  dateOfLastSignificantInteraction?: string | null;
  goalsMovingForward?: string | null;
  peopleInvolved?: string | null;
}
// ─── Request params for the API hook ──────────────────────────────────────────
export interface GetFlightRiskParams {
  orgId: string;
  subOrgId: string;
}

// ─── Editable Flight Risk Fields & Update Payload ────────────────────────────
export const EDITABLE_FLIGHT_RISK_FIELDS = [
  "recap_of_plan_of_action_poa_details_and_next_steps_if_applicable",
  "level_of_urgency_for_this_flight_risk",
  "last_30_days_return_on_ad_spend_roas",
  "kpi_that_best_represents_the_main_issue",
  "flight_risk_status",
  "does_the_account_have_a_walmart_am_or_connect_partner",
  "main_issue_reason_for_flight_risk_flag",
  "on_a_scale_of_1_to_5_how_confident_are_you_in_retaining_this_client_with_the_current_plan_of_action_poa",
  "last_30_days_of_sales",
  "which_of_the_following_factors_primarily_contributed_to_the_flight_risk",
  "is_a_plan_of_action_poa_in_place",
  "account_manager_email",
  "hub_leader_email",
  "email_address",
  "ppc_manager_email",
  "strategist_email",
  "goals_moving_forward",
  "people_involved",
  "date_of_last_significant_interaction",
] as const;

export type EditableFlightRiskFieldKey = (typeof EDITABLE_FLIGHT_RISK_FIELDS)[number];

export interface EditableFlightRiskFields {
  recap_of_plan_of_action_poa_details_and_next_steps_if_applicable?: string | null;
  level_of_urgency_for_this_flight_risk?: string | null;
  last_30_days_return_on_ad_spend_roas?: string | null;
  kpi_that_best_represents_the_main_issue?: string | null;
  flight_risk_status?: string | null;
  does_the_account_have_a_walmart_am_or_connect_partner?: string | null;
  main_issue_reason_for_flight_risk_flag?: string | null;
  on_a_scale_of_1_to_5_how_confident_are_you_in_retaining_this_client_with_the_current_plan_of_action_poa?: string | null;
  last_30_days_of_sales?: string | null;
  which_of_the_following_factors_primarily_contributed_to_the_flight_risk?: string | null;
  is_a_plan_of_action_poa_in_place?: string | null;
  account_manager_email?: string | null;
  hub_leader_email?: string | null;
  email_address?: string | null;
  ppc_manager_email?: string | null;
  strategist_email?: string | null;
  goals_moving_forward?: string | null;
  people_involved?: string | null;
  date_of_last_significant_interaction?: string | null;

  [key: string]: string | null | undefined;
}

export interface UpdateFlightRiskFieldsPayload {
  subOrgId: string;
  orgId: string;
  fieldValues: EditableFlightRiskFields;
}

/** Props for the FlightRiskAssessment content component */
export interface FlightRiskAssessmentProps {
  orgId: string | null;
  subOrgId: string | null;
  setFlightRiskTimestamp?: (timestamp: string | null) => void;
  isCreateMode?: boolean;
  accountManagerName?: string;
  isFlightRiskCreateUpdateEnabled?: boolean;
  onHeaderActionsChange?: (actions: ReactNode) => void;
  onEditModeChange?: (isEditing: boolean) => void;
  onClose?: () => void;
  onCreateSuccess: () => void;
}

export enum FlightRiskQuestions {
  URGENCY = "Level of urgency for this flight risk:",
  CONFIDENCE = "On a scale of 1–5, how confident are you in retaining this client with the current POA?",
  WALMART_AM = "Does the account have a Walmart AM or connect partner?",
  MAIN_ISSUE = "Main issue / reason for flight risk flag:",
  CONTRIBUTING_FACTOR = "Which of the following factors primarily contributed to the flight risk?",
  KPI = "KPI that best represents the main issue:",
  SALES = "Last 30 days of sales:",
  ROAS = "Last 30 days return on ad spend (ROAS):",
  POA = "Is a plan of action (POA) in place?",
  POA_RECAP = "Recap of plan of action (POA) details and next steps:",
  EMAIL = "Email",
  HUB_LEADER = "Hub Leader:",
  STRATEGIST = "Strategist",
  PPC_MANAGER = "PPC Manager:",
  AM = "AM:",
  DATE_OF_LAST_SIGNIFICANT_INTERACTION = "Date of last significant interaction",
  GOALS_MOVING_FORWARD = "Goals moving forward",
  PEOPLE_INVOLVED = "People involved",
}

// ─── Form state for edit / create modes ───────────────────────────────────────
export interface FlightRiskFormState {
  email: string;
  hubLeader: string;
  strategist: string;
  ppcManager: string;
  urgency: string;
  confidence: string;
  walmartAm: string;
  mainIssue: string;
  contributingFactor: string;
  /** Free-text KPI field (comma-separated if multiple) */
  kpi: string;
  sales: string;
  roas: string;
  poaInPlace: string;
  /** Newline-separated POA recap text */
  poaRecap: string;
  dateOfLastInteraction: string;
  goalsMovingForward: string;
  /** Free-text people involved field */
  peopleInvolved: string;
}

export interface FlightRiskFormErrors {
  urgency?: string;
  confidence?: string;
}

// ─── Score Tier Types (3 States: High, Medium, Low) ───────────────────────────
export type ScoreTier = "high" | "medium" | "low";

export interface ScoreStyleClasses {
  circleClass: string;
  dotClass: string;
  scoreTextClass: string;
}

export interface RatingScoreData {
  rawValue: string | null;
  score: number | null;
  isOverMax: boolean;
  isValidRating: boolean;
}
